import type { RefObject } from 'react';
import { useEffect } from 'react';

export type EventType =
  | 'focusin'
  | 'focusout'
  | 'mousedown'
  | 'mouseup'
  | 'touchend'
  | 'touchstart';

export const useOnClickOutside = (
  ref: RefObject<HTMLElement | null | undefined>,
  handler: (event: Event) => unknown,
  eventTypes: EventType | EventType[] = ['mousedown', 'touchend'],
) => {
  useEffect(() => {
    const listener = (event: Event): void => {
      // don't do anything if the click event is on the ref element or descendent elements
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }

      handler(event);
    };

    if (Array.isArray(eventTypes)) {
      eventTypes.forEach((eventType) => {
        document.addEventListener(eventType, listener);
      });
    } else {
      document.addEventListener(eventTypes, listener);
    }

    return () => {
      if (Array.isArray(eventTypes)) {
        eventTypes.forEach((eventType) => {
          document.removeEventListener(eventType, listener);
        });
      } else {
        document.removeEventListener(eventTypes, listener);
      }
    };
  }, [ref, handler, eventTypes]);
};
